import * as vp from 'vopay';
import { SideDrawer } from 'vopay';
import { FunctionComponent, JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReportCreationForm from '../../ReportCreationForm';

import { CreateReportFormData, CreateReportRequest } from '../../types';
import { useClientData } from '@/providers/ClientData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import usePostCreateReport from '@/hooks/useApi/usePostCreateReport';
import { Button } from 'UI';

type ReportCreationDialogueProps = {
  title: string | JSX.Element;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (id?: string) => void;
  disabledFields?: (keyof CreateReportRequest)[];
  initialFields?: Partial<CreateReportFormData>;
  subscriptions: vp.SubscriptionItem[];
};

type FC = FunctionComponent<ReportCreationDialogueProps>;
const ReportCreationDialogue: FC = ({
  title,
  isOpen,
  onClose,
  onSuccess,
  disabledFields = [],
  initialFields,
  subscriptions,
}: ReportCreationDialogueProps) => {
  const { response } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.SideDrawer' });
  const alertDialog = useAlertDialog();

  const [, , postCreateReport] = usePostCreateReport({});
  const [isDisabled, setIsDisabled] = useState(true);

  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);

  const defaultValues: CreateReportFormData = useMemo(() => {
    return {
      Frequency: '',
      RecipientEmails: response.LoginUserName,
      Description: '',
      PaymentMethods: [],
      ScheduleType: 'one-time',
      DateFormat: 'DD-MM-YYYY',
      FromDate: startDate.getTime().toString(),
      ToDate: Date.now().toString(),
      IncludeSubAccounts: false,
      IncludeGraph: false,
      DateRangeOption: '',
      AccountTransactionReportType: '',
      TransactionReportFailureReasons: [],
      ...initialFields,
      ReportName: '',
    };
  }, [initialFields]);

  const submitReport = async (values: CreateReportRequest, reset: () => void) => {
    try {
      const res = await postCreateReport({
        ...values,
        PortalUser: response.LoginUserName || '',
        // TODO: this should really be in serializeField
        PaymentRails: [
          'ApprovalActivityReport',
          'AccountingSoftwareActivityReport',
          'InvoiceReport',
          'TransactionSuccessAndFailureAnalysisReport',
          'EndOfDayBalance',
          'InterestReport',
          'GLCodeTransactionSummary',
          'GLCodeSummaryReport',
          'TransactionCodeSummary',
          'TransactionSummaryByCode',
          'TransactionFailureDetailReport',
        ].includes(values.AccountTransactionReportType)
          ? JSON.stringify([])
          : JSON.stringify(values.PaymentMethods.split(',')),
        FromDate:
          values.ScheduleType === 'one-time' && values.DateRangeOption === 'CustomDateRange'
            ? values.FromDate || ''
            : '',
        ToDate:
          values.ScheduleType === 'one-time' && values.DateRangeOption === 'CustomDateRange' ? values.ToDate || '' : '',
        ScheduleType: values.ScheduleType === 'one-time' ? 'one-time' : values.ScheduleType || '',
        TransactionReportFailureReasons:
          values.AccountTransactionReportType === 'TransactionFailureDetailReport'
            ? JSON.stringify(values.TransactionReportFailureReasons)
            : JSON.stringify([]),
      });
      if (res.Success) {
        reset();
        onClose();
        if (onSuccess) onSuccess(res.AccountSubscriptionReport.ID);
      } else {
        alertDialog.error('Error', res.ErrorMessage, {
          abortLabel: 'Close',
        });
      }
    } catch (err) {
      console.error(err);

      const msg = err instanceof Error ? err.message : null;

      alertDialog.error('Error', msg, {
        abortLabel: 'Close',
      });
    }
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button colorScheme="primary" form="save-subscription" type="submit" disabled={isDisabled}>
            {t('createReport')}
          </Button>
        </div>
      }
    >
      <ReportCreationForm
        defaultValues={defaultValues}
        disabledFields={disabledFields}
        onSubmit={submitReport}
        subscriptions={subscriptions}
        onValidate={(isValid) => {
          setIsDisabled(!isValid);
        }}
      />
    </SideDrawer>
  );
};

export default ReportCreationDialogue;
